import { ChangeDetectionStrategy, Component, OnInit, Pipe, PipeTransform, signal } from "@angular/core";
import { BehaviorSubject, combineLatest, finalize, map, Observable, of, Subject, switchMap } from "rxjs";
import { DemoNewsService } from "../services/demo-news.service";
import { getTextColorUtils } from "../shared/color-utils";
import { NewsPaginationDto, NewsUpdateViewModel, NewsViewModel } from "../core/model/DemoNews";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { UpdateSelecteItems } from "../shared/filter-dropdown/filter-dropdown.component";
import { MatDialog } from "@angular/material/dialog";
import { NewsPopupComponent } from "./news-popup/news-popup.component";
import { DemoAssetViewModel } from "../core/model/ContentManagement";
import { DemoAssetService } from "../services/demo-asset.service";
import { ValueChangedEvent } from "devextreme/ui/tag_box";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

interface NewsHomePageModel {
  id: number;
  title: string;
  description: string;
  categoryName: string;
  categoryColor: string;
  categoryBackground: string;
  publishDate: Date;
  size: string;
  updates: NewsUpdateViewModel[];
}

interface CategoryHomePageModel {
  id: number;
  name: string;
  color: string;
  textColor: string;
}

@Pipe({ name: "safeHtml" })
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitized: DomSanitizer) {}
  transform(value: string): SafeHtml {
    return this.sanitized.bypassSecurityTrustHtml(value);
  }
}

@Component({
  selector: "brd-news",
  templateUrl: "./news.component.html",
  styleUrl: "./news.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsComponent implements OnInit {
  private loadNewsSubject = new BehaviorSubject<number>(0);
  private newsPaginationSubject = new Subject<NewsPaginationDto>();

  protected news$ = combineLatest([this.newsPaginationSubject.asObservable(), this.demoNewsService.newsToRead$]).pipe(
    map(([res, newsToRead]) => {
      this.pageNews.set([...res.news]);
      return res.news.map((item, i) => {
        const mappedNews = this.mapNews(item, i);
        return { ...mappedNews, isToRead: newsToRead.some(x => x.id === item.id) };
      });
    })
  );
  private pageNews = signal<NewsViewModel[]>([]);

  private categories$: Observable<CategoryHomePageModel[]> = this.demoNewsService.getNewsCategories().pipe(
    map(categories =>
      categories.map(category => ({
        id: category.id,
        name: category.name,
        color: category.color,
        textColor: getTextColorUtils(category.color)
      }))
    )
  );
  private assets$: Observable<DemoAssetViewModel[]> = this.assetService.getAssets();

  protected filters$ = combineLatest([this.categories$, this.assets$]).pipe(
    map(([categories, assets]) => {
      return { categories: categories, assets: assets };
    })
  );

  protected pages = signal<number[]>([]);

  protected selectedCategories: number[] = [];
  protected selectedAssets: number[] = [];
  protected searchText = "";
  protected loading = signal(false);
  protected pageSize = 10;
  protected currentPage = 0;
  protected totalPages = 0;
  protected delayTimeout?: ReturnType<typeof setTimeout>;

  protected searchButtonOptions = {
    icon: "search",
    stylingMode: "text",
    type: "noraml",
    hoverStateEnabled: false,
    activeStateEnabled: false,
    elementAttr: { class: "search-icon" }
  };

  constructor(
    private readonly demoNewsService: DemoNewsService,
    private readonly assetService: DemoAssetService,
    private readonly dialog: MatDialog
  ) {
    this.loadNewsSubject
      .asObservable()
      .pipe(
        switchMap(pos => {
          return this.getNewsPaginationObservable(pos).pipe(
            finalize(() => {
              this.loading.set(false);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public ngOnInit(): void {
    this.loadNews();
  }

  protected clearFilter(): void {
    this.selectedCategories = [];
    this.selectedAssets = [];
    this.searchText = "";
    this.loadNews();
  }

  protected applyFilter(): void {
    this.loadNews();
  }

  protected onSearchChange(): void {
    if (this.delayTimeout) {
      clearTimeout(this.delayTimeout);
    }
    this.delayTimeout = setTimeout(() => {
      this.currentPage = 0;
      this.applyFilter();
    }, 1000);
  }

  protected selectCategory(categoryId: number): void {
    const index = this.selectedCategories.indexOf(categoryId);
    if (index === -1) {
      this.selectedCategories.push(categoryId);
    } else {
      this.selectedCategories.splice(index, 1);
    }

    this.currentPage = 0;
    this.applyFilter();
  }

  protected selectedDemoAssetsChanges(event: ValueChangedEvent): void {
    this.selectedAssets = event.value;
    this.currentPage = 0;
    this.applyFilter();
  }

  protected updateSelectedCategories({ value, action }: UpdateSelecteItems): void {
    switch (action) {
      case "ADD":
        this.selectedCategories.push(value.id);
        break;
      case "REMOVE":
        this.selectedCategories = this.selectedCategories.filter(x => x !== value.id);
        break;
    }
  }

  protected openNewsPopup(news: NewsHomePageModel, i: number): void {
    const popupInfoSubject = new Subject<{ newsId: number; action: "prev" | "next" }>();
    const dialogRef = this.dialog.open(NewsPopupComponent, {
      data: {
        title: news.title,
        id: news.id,
        action: (currentId: number, action: "prev" | "next") => popupInfoSubject.next({ newsId: currentId, action: action }),
        infoPopup$: popupInfoSubject.asObservable().pipe(
          switchMap(({ newsId, action }) => {
            const loadedNews = this.pageNews();
            const currentIndex = loadedNews.findIndex(x => x.id === newsId);

            let res: Observable<NewsViewModel[]>;
            let newIndex: number;
            if ((action === "prev" && currentIndex === 0) || (action === "next" && currentIndex === loadedNews.length - 1)) {
              newIndex = action === "prev" ? this.pageSize - 1 : 0;
              res = this.getNewsPaginationObservable(action === "prev" ? this.currentPage - 2 : this.currentPage).pipe(map(x => x.news));
            } else {
              newIndex = action === "prev" ? currentIndex - 1 : currentIndex + 1;
              res = of(loadedNews);
            }

            return combineLatest([res, of(newIndex)]);
          }),
          map(([n, j]) => {
            const item = n[j];

            return {
              id: item?.id,
              title: item?.title,
              hasPrev: j > 0 || this.currentPage > 1,
              hasNext: j < n.length - 1 || this.currentPage < this.totalPages,
              publishDate: item?.publishDate
            };
          })
        ),
        publishDate: news.publishDate,
        hasPrev: i !== 0 || this.currentPage > 1,
        hasNext: i !== this.pageNews().length - 1 || this.currentPage < this.totalPages
      },
      panelClass: "news-popup",
      width: "1078px",
      height: "726px",
      maxWidth: "80vw",
      maxHeight: "80vh",
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(() => {
      this.demoNewsService.getNewsToRead().subscribe();
    });
  }

  protected loadNews(position: number = 0): void {
    if (position + 1 === this.currentPage) return;

    this.loading.set(true);
    this.loadNewsSubject.next(position);
  }

  private getNewsPaginationObservable(position: number): Observable<NewsPaginationDto> {
    return this.demoNewsService
      .getNewsPagination(
        this.pageSize,
        position * this.pageSize,
        this.searchText,
        this.selectedCategories ?? undefined,
        this.selectedAssets ?? undefined
      )
      .pipe(
        map(res => {
          this.totalPages = res.total ? Math.ceil(res.total / this.pageSize) : 0;
          this.currentPage = res.total ? position + 1 : 0;

          let pages: number[] = [];
          if (this.currentPage === 1) pages = [1, 2, 3];
          else if (this.currentPage === this.totalPages) pages = [this.totalPages - 2, this.totalPages - 1, this.totalPages];
          else pages = [this.currentPage - 1, this.currentPage, this.currentPage + 1];

          this.pages.set(pages.filter(x => x > 0 && x <= this.totalPages));
          this.newsPaginationSubject.next(res);
          return res;
        })
      );
  }

  private mapNews(news: NewsViewModel, index: number): NewsHomePageModel {
    index = index + 1;
    return {
      id: news.id,
      title: news.title,
      description: news.description,
      categoryName: news.category?.name ?? "",
      categoryColor: news.category?.color ? getTextColorUtils(news.category.color) : "",
      categoryBackground: news.category?.color ?? "",
      publishDate: new Date(news.publishDate),
      size: index % 5 === 1 || index % 5 === 2 ? "large" : "small",
      updates: news.updates
    };
  }
}
